/**
 * Push Notification Queue Tab
 * Displays and manages queued push notifications
 */

import { useState, useMemo, useEffect } from 'react';
import { IconBell, IconEye, IconEdit, IconTrash, IconRefresh, IconClock, IconUser, IconDeviceMobile } from '@tabler/icons-react';
import { Stack, Group, Text, Button, ActionIcon, Badge, Avatar } from '../../../../../components/ui';
import { DataTable } from '../../../../../components/advanced/data';
import type { DataTableColumn } from '../../../../../components/advanced/data/DataTable';
import { EmptyState } from '../../../../../components/ui/data-display';
import { alert } from '../../../../../components/ui/providers/AlertProvider';
import type { PushQueueTabProps, PushQueueRow } from '../types';
import { mapPushToRow, getTargetValue, decodePushBody, statusOf, recipientName } from '../types';
import { getStatusColor, formatDateTime } from '../../types';

export function PushQueueTab({
  queueItems,
  loading,
  onRefresh,
  onView,
  onEdit,
  onDelete,
  onBulkDelete,
  onRetry,
  onUpdatePriority,
}: PushQueueTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Newest first. The API returns rows in cache/insertion order, so without this the outbox opens on
  // the oldest pushes and you have to page to the end to see what just went out.
  const rows: PushQueueRow[] = useMemo(
    () =>
      queueItems
        .map(mapPushToRow)
        .sort((a, b) =>
          // createdDateTime is a yyyyMMddHHmmss stamp, so it sorts numerically. Same-second rows fall
          // back to messageID, which is monotonic.
          (Number(b.createdDateTime) || 0) - (Number(a.createdDateTime) || 0) ||
          (b.messageID || 0) - (a.messageID || 0)
        ),
    [queueItems]
  );

  const filteredRows = useMemo(() => {
    if (!searchQuery) return rows;
    const query = searchQuery.toLowerCase();
    return rows.filter((row) => {
      const decodedBody = decodePushBody(row.body);
      return (
        row.title?.toLowerCase().includes(query) ||
        decodedBody.toLowerCase().includes(query) ||
        statusOf(row).toLowerCase().includes(query) ||
        getTargetValue(row).toLowerCase().includes(query)
      );
    });
  }, [rows, searchQuery]);

  // Keep the page in range when rows disappear (deletes, refresh, a narrower search),
  // otherwise the table can strand the user on an empty page past the end.
  useEffect(() => {
    if (pageSize <= 0) return;
    const lastPage = Math.max(1, Math.ceil(filteredRows.length / pageSize));
    if (page > lastPage) setPage(lastPage);
  }, [filteredRows.length, page, pageSize]);

  const columns: DataTableColumn<PushQueueRow>[] = useMemo(
    () => [
      {
        key: 'title',
        label: 'Title',
        width: 200,
        sortable: true,
        render: (value) => (
          <Group gap="xs">
            <IconBell size={16} />
            <Text size="sm" fw={500} lineClamp={2}>{value || 'No Title'}</Text>
          </Group>
        ),
      },
      // The message body is deliberately not a column — open a row to read it. Search still matches
      // on the body (see filteredRows).
      {
        key: 'userId',
        label: 'Recipient',
        width: 250,
        render: (_, row) => {
          const name = recipientName(row);
          const secondary = row.userEmail || row.userCellphone;

          // No owner: the device is no longer registered to anyone. Show the device instead of a bare
          // GUID under a "User ID" label, which is what it used to be.
          if (!name && !secondary) {
            return (
              <Group gap={6} wrap="nowrap">
                <IconDeviceMobile size={14} />
                <Stack gap={0} style={{ minWidth: 0 }}>
                  <Text size="xs" c="dimmed">Unregistered device</Text>
                  <Text size="xs" c="dimmed" lineClamp={1} style={{ fontFamily: 'monospace' }}>
                    {row.deviceId || '—'}
                  </Text>
                </Stack>
              </Group>
            );
          }

          return (
            <Group gap="xs" wrap="nowrap">
              <Avatar src={row.userAvatar || undefined} radius="xl" size="sm">
                {name ? name.charAt(0).toUpperCase() : <IconUser size={14} />}
              </Avatar>
              <Stack gap={0} style={{ minWidth: 0 }}>
                <Text size="sm" fw={500} lineClamp={1}>{name || 'Unknown user'}</Text>
                {secondary && <Text size="xs" c="dimmed" lineClamp={1}>{secondary}</Text>}
              </Stack>
            </Group>
          );
        },
      },
      {
        key: 'status',
        label: 'Status',
        width: 190,
        sortable: true,
        filterable: true,
        render: (_, row) => {
          // Fall back to deliveryStatus for legacy rows written before the sender recorded a status.
          const status = statusOf(row);
          return (
            <Stack gap={2}>
              <Group gap={4} wrap="nowrap">
                {/*
                  getStatusColor returns the theme's semantic names (success/info/warning/danger),
                  which belong on `gradientVariant` — Mantine's `color` cannot resolve them and the
                  badge ended up with white text on a white background.
                */}
                <Badge gradientVariant={getStatusColor(status as any)} pulse={status === 'Pending'}>
                  {status}
                </Badge>
                {row.retriesExhausted && status === 'Failed' && (
                  <span title="Retry budget exhausted — the worker will not pick this up again on its own. Use Retry.">
                    <Badge size="xs" variant="light" color="gray">
                      no auto-retry
                    </Badge>
                  </span>
                )}
              </Group>
              {status === 'Failed' && (row.failureReason || row.errorMessage) && (
                <Text size="xs" c="dimmed" lineClamp={2} title={row.failureReason || row.errorMessage || ''}>
                  {row.failureReason || row.errorMessage}
                </Text>
              )}
            </Stack>
          );
        },
      },
      {
        key: 'createdDateTime',
        label: 'Queued',
        width: 170,
        sortable: true,
        render: (value) => (
          <Group gap="xs">
            <IconClock size={14} />
            <Text size="sm">{formatDateTime(value)}</Text>
          </Group>
        ),
      },
      {
        key: 'actions',
        label: 'Actions',
        width: 150,
        render: (_, row) => {
          const status = statusOf(row);
          return (
            <Group gap="xs">
              <ActionIcon variant="subtle" color="blue" onClick={(e) => { e.stopPropagation(); onView(row); }} title="View"><IconEye size={16} /></ActionIcon>
              {status === 'Pending' && <ActionIcon variant="subtle" color="gray" onClick={(e) => { e.stopPropagation(); onEdit(row); }} title="Edit"><IconEdit size={16} /></ActionIcon>}
              {status === 'Failed' && (
                <ActionIcon
                  variant="subtle"
                  color="green"
                  title={`Retry — ${row.failureReason || row.errorMessage || 'send failed'}`}
                  onClick={async (e) => {
                    e.stopPropagation();
                    const confirmed = await alert.confirm(
                      'Retry Push Notification',
                      `Requeue #${row.messageID} for delivery?\n\nLast failure: ${row.failureReason || row.errorMessage || 'unknown'}`,
                      { confirmText: 'Retry' }
                    );
                    if (confirmed) await onRetry(row.messageID);
                  }}
                >
                  <IconRefresh size={16} />
                </ActionIcon>
              )}
              <ActionIcon variant="subtle" color="red" onClick={async (e) => { e.stopPropagation(); if (await alert.confirm('Delete Notification', `Delete?`, { confirmText: 'Delete' })) await onDelete(row.messageID); }} title="Delete"><IconTrash size={16} /></ActionIcon>
            </Group>
          );
        },
      },
    ],
    [onView, onEdit, onDelete, onRetry, onUpdatePriority]
  );

  if (!loading && queueItems.length === 0) {
    // Keep Refresh reachable here too — an empty outbox is exactly when you want to re-check.
    return (
      <Stack gap="md">
        <Group justify="flex-end">
          <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={() => onRefresh()} loading={loading}>
            Refresh
          </Button>
        </Group>
        <EmptyState title="No Queued Push Notifications" description="Your push notification queue is empty." icon={<IconBell size={48} />} />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">{filteredRows.length} push notification(s) in queue</Text>
        <Group gap="xs">
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            onClick={() => onRefresh()}
            loading={loading}
          >
            Refresh
          </Button>
        {selectedRows.length > 0 && (
          <Button
            color="red"
            variant="subtle"
            onClick={async () => {
              const confirmed = await alert.confirm(
                'Delete Selected',
                `Delete ${selectedRows.length} push notification(s)? This cannot be undone.`,
                { confirmText: 'Delete All' }
              );
              if (!confirmed) return;
              // One request for the whole selection — not one call per row.
              await onBulkDelete(selectedRows.map(Number));
              setSelectedRows([]);
            }}
          >
            Delete Selected ({selectedRows.length})
          </Button>
        )}
        </Group>
      </Group>
      <DataTable
        data={filteredRows}
        columns={columns}
        loading={loading}
        selectable
        selectedRows={selectedRows}
        onSelectionChange={setSelectedRows}
        searchPlaceholder="Search by title, message, or target..."
        searchValue={searchQuery}
        onSearch={(query) => { setSearchQuery(query); setPage(1); }}
        onRowClick={(row) => onView(row)}
        pagination={{
          page,
          pageSize,
          total: filteredRows.length,
          onPageChange: setPage,
          onPageSizeChange: (size) => { setPageSize(size); setPage(1); },
        }}
        gradientVariant="primary"
        highlightOnHover
        striped
        withRowAnimations
        enableInternalSort
        enableInternalSearch
      />
    </Stack>
  );
}
